import { useState } from "react";
import { Calculator, CreditCard, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/common";
import { AddressForm } from "@/components/boy/AddressForm";
import { brl, digits, errMsg, maskCep } from "@/lib/api";
import { addrMissing, EMPTY_ADDR, fmtKm } from "@/lib/boy";
import { PhotoPick } from "./PhotoPick";
import { CustomerBox, EMPTY_CUSTOMER } from "./CustomerBox";

function QuoteBox({ quote }) {
  const approx = quote.distance_source !== "rota" || [quote.origin, quote.dest].some((a) => !["endereco", "mapa"].includes(a.precision));
  return (
    <div className="grid grid-cols-2 gap-3 rounded-xl border-2 border-primary/30 bg-primary/5 p-4" data-testid="boy-quote-box">
      <div><p className="eyebrow">Distância</p><p className="text-2xl font-bold" data-testid="boy-quote-km">{fmtKm(quote.distance_km)} km</p></div>
      <div className="text-right"><p className="eyebrow">Valor da corrida</p><p className="text-2xl font-bold text-primary" data-testid="boy-quote-price">{brl(quote.price)}</p></div>
      <p className="col-span-2 text-xs text-muted-foreground" data-testid="boy-quote-note">{approx ? "Distância aproximada: confira rua e número para um cálculo mais preciso." : "Distância calculada pelo caminho das ruas."}</p>
    </div>
  );
}

const Stop = ({ icon, title, children }) => <div className="space-y-3"><p className="flex items-center gap-2 text-sm font-semibold">{icon}{title}</p>{children}</div>;

export function RideRequest({ bx, merchant, canPay }) {
  const [origin, setOrigin] = useState(() => ({ ...EMPTY_ADDR, ...merchant.address, cep: maskCep(merchant.address?.cep || "") }));
  const [dest, setDest] = useState(EMPTY_ADDR);
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState("");
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const edit = (setter) => (v) => { setter(v); setQuote(null); };
  const body = () => ({ origin: { ...origin, cep: digits(origin.cep) }, dest: { ...dest, cep: digits(dest.cep) } });
  const calc = async () => {
    const m = addrMissing(origin, "retirada") || addrMissing(dest, "entrega");
    if (m) return toast.error(m);
    setBusy("quote");
    try {
      setQuote((await bx.post("/quote", body())).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const confirm = async () => {
    const wa = digits(customer.whatsapp);
    if (wa && wa.length < 10) return toast.error("Informe o WhatsApp do cliente com DDD");
    setBusy("pay");
    try {
      const { data } = await bx.post("/rides", { ...body(), notes, photo: photo && { path: photo.path, content_type: photo.content_type }, origin_url: window.location.origin,
        customer: wa ? { name: customer.name, whatsapp: wa, save: customer.save } : null });
      window.location.href = data.init_point;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };
  const spin = <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
  return (
    <section className="card-surface space-y-6 p-5 sm:p-6" data-testid="boy-request-card">
      <div><p className="eyebrow">Nova entrega</p><h2 className="text-lg font-bold">Solicitar motoboy</h2></div>
      {!canPay && <p className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300" data-testid="boy-cannot-pay">A empresa ainda não ativou o pagamento online. Você pode calcular o valor, mas só consegue pedir depois que ela ativar.</p>}
      <Stop icon={<span className="h-3 w-3 rounded-full border-2 border-primary" />} title="Retirada (origem)"><AddressForm value={origin} onChange={edit(setOrigin)} id="boy-origin" /></Stop>
      <Stop icon={<MapPin className="h-4 w-4 text-red-500" />} title="Entrega (destino)"><AddressForm value={dest} onChange={edit(setDest)} id="boy-dest" /></Stop>
      <CustomerBox bx={bx} value={customer} onChange={setCustomer} onAddress={edit(setDest)} />
      <Field label="Observações para o motoboy (opcional)"><Textarea rows={2} maxLength={300} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex.: falar com a recepção, item frágil" data-testid="boy-notes-input" /></Field>
      <PhotoPick bx={bx} value={photo} onChange={setPhoto} />
      {quote && <QuoteBox quote={quote} />}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button variant={quote ? "outline" : "default"} className="h-11" onClick={calc} disabled={!!busy} data-testid="boy-quote-btn">{busy === "quote" ? spin : <Calculator className="mr-2 h-4 w-4" />}{quote ? "Recalcular" : "Calcular valor"}</Button>
        {quote && <Button className="h-11 flex-1 text-base font-semibold" onClick={confirm} disabled={!!busy || !canPay} data-testid="boy-confirm-btn">{busy === "pay" ? spin : <CreditCard className="mr-2 h-4 w-4" />}Confirmar e pagar {brl(quote.price)}</Button>}
      </div>
    </section>
  );
}
